import React, { FC, useEffect, useState } from "react"
import {
    Button,
    Dialog,
    DialogHeader,
    DialogDescription,
    Input,
    Grid,
    GridRow,
    InputProps,
    useIntl,
    Icon,
    DialogActions,
    DialogContent,
} from "@metsooutotec/modes-react-components"
import customerStore, { CustomerStoreResults } from "features/organizations/customers/store/customer-store"
import { validateEmail, validateText, validateAddress, validateHours, validateSiteLongitudeLatitudeDecimal } from "validators/inputValidators"
import { addressParss, ModalTypes, validateSiteForm } from "features/organizations/customers/utils/customer-modal-helper"
import { handleUploadFileClick, NavigationModalOptions, resetRecordFormData } from "components/utils/modal-helper"
import { Address, CreateEditSite, Site } from "../models/sites-models"
import { observer } from "mobx-react-lite"

interface SiteModalProps {
    isOpen: boolean | undefined
    onClose: (passed: boolean, customerId?: string, navigationCustomerOpetions?: NavigationModalOptions) => void
    site?: Site
    customerId?: string
}

const SiteModal: FC<SiteModalProps> = observer(({ isOpen, onClose, site, customerId }) => {
    const { formatMessage } = useIntl()
    const { editEntity, createEntity, customerLoading } = customerStore
    const [isEditing, setIsEditing] = useState(false)
    const [isFormModified, setFormModified] = useState(false)
    const [isError, setIsError] = useState("")
    const [selectedFlowSheet, setSelectedFlowSheet] = useState<File | null>(null)
    const [formData, setFormData] = useState<Record<string, string>>({})
    const [formErrors, setFormErrors] = useState<Record<string, string>>({})

    // ------------------ Setup & Reset ------------------
    useEffect(() => {
        if (site) {
            setIsEditing(true)
            setFormData({
                siteName: site.name ?? "",
                address: site.address ? `${site.address.country}, ${site.address.state}, ${site.address.region}` : "",
                contact: site.contact ?? "",
                metsoContact: site.metsoContact ?? "",
                latitude: site.address?.latitude?.toString() ?? "",
                longitude: site.address?.longitude?.toString() ?? "",
                utcOffsetHours: site.utcOffsetHours?.toString() ?? "",
                uploadFlowSheetName: selectedFlowSheet?.name ?? "",
            })
        } else {
            setIsEditing(false)
            resetForm()
        }
        setFormModified(false)
    }, [site])

    const resetForm = () => {
        resetRecordFormData(setFormData)
        resetRecordFormData(setFormErrors)
        setSelectedFlowSheet(null)
        setIsError("")
    }

    const handleChange = (id: string, value: string) => {
        setFormData(prev => ({ ...prev, [id]: value }))
        setFormModified(true)
        setIsError("")
    }

    const handleValidation = (id: string, error?: string) => {
        setFormErrors(prev => ({ ...prev, [id]: error ?? "" }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (isEditing && !isFormModified && !selectedFlowSheet) {
            setIsError("No changes made. Please update the form before submitting.")
            return
        }

        const errors = validateSiteForm(formData)
        const hasErrors = Object.values(errors).some(e => e)
        if (hasErrors) {
            setFormErrors(errors)
            setIsError("There are validation errors.")
            return
        }

        const { siteName, contact, metsoContact, address, longitude, latitude, utcOffsetHours } = formData
        const parsedAddress: Address = {
            ...addressParss(address),
            longitude,
            latitude,
        }

        const payload: CreateEditSite = {
            customerId: customerId ?? "",
            address: parsedAddress,
            utcOffsetHours,
            flowSheetImage: selectedFlowSheet ?? new File([], "empty"),
            name: siteName,
            contact,
            metsoContact,
        }

        const result: CustomerStoreResults = isEditing
            ? await editEntity({ ...payload, id: site?.id }, ModalTypes.SiteModal)
            : await createEntity(payload, ModalTypes.SiteModal)

        if (!result.passed) {
            setIsError(result.message)
            return
        }

        onClose(true, result.entityId, { type: isEditing ? "edited" : "created" })
        resetForm()
    }

    const labels = {
        siteName: formatMessage("siteModal.name"),
        address: formatMessage("siteModal.address"),
        contact: formatMessage("siteModal.contact"),
        metsoContact: formatMessage("siteModal.metsoContact"),
        latitude: formatMessage("siteModal.latitude"),
        longitude: formatMessage("siteModal.longitude"),
        utcOffsetHours: formatMessage("siteModal.utcOffsetHours"),
        uploadFlowSheet: formatMessage("siteModal.uploadFlowSheet"),
        confirm: formatMessage("modal.confirm"),
        cancel: formatMessage("modal.cancel"),
        header: isEditing ? formatMessage("siteModal.headerEdit") : formatMessage("siteModal.header"),
        description: formatMessage("modal.description"),
    }

    const fieldConfig: {
        id: string
        label: string
        type: string
        validate?: (v: string) => string
        isRequired?: boolean
        isFileField?: boolean
    }[] = [
        { id: "siteName", label: labels.siteName, type: "string", validate: validateText, isRequired: true },
        { id: "address", label: labels.address, type: "string", validate: validateAddress },
        { id: "longitude", label: labels.longitude, type: "string", validate: validateSiteLongitudeLatitudeDecimal },
        { id: "latitude", label: labels.latitude, type: "string", validate: validateSiteLongitudeLatitudeDecimal },
        { id: "utcOffsetHours", label: labels.utcOffsetHours, type: "string", validate: validateHours, isRequired: true },
        { id: "contact", label: labels.contact, type: "email", validate: validateEmail },
        { id: "metsoContact", label: labels.metsoContact, type: "email", validate: validateEmail, isRequired: true },
        { id: "uploadFlowSheetName", label: labels.uploadFlowSheet, type: "string", isFileField: true },
    ]

    const renderField = (field: (typeof fieldConfig)[number]) => {
        const value = formData[field.id] ?? ""
        const error = formErrors[field.id]
        const isOptional = !field.isRequired && !value

        const icon = field.isFileField ? (
            <>
                {value && <Icon className='icon-cross-circle-fill-16' onClick={clearFlowSheetFile} />}
                <Icon className='icon-upload-16' onClick={handleUploadFlowSheetClick} />
            </>
        ) : (
            value && <Icon className='icon-cross-circle-fill-16' onClick={() => handleChange(field.id, "")} />
        )

        const props: InputProps = {
            id: field.id,
            label: field.label,
            placeholder: `Type ${field.label}...`,
            value,
            onChange: e => handleChange(field.id, e.target.value),
            onValidation: isOptional ? undefined : e => handleValidation(field.id, e),
            validate: isOptional ? undefined : field.validate,
            required: field.isRequired,
            uiRequired: field.isRequired,
            error,
            type: field.type,
            heightClass: "medium",
            spanning: true,
            icon,
            readOnly: field.isFileField,
        }

        return (
            <GridRow key={field.id} style={{ marginBottom: "var(--size-m)" }}>
                <Input {...props} />
            </GridRow>
        )
    }

    const handleUploadFlowSheetClick = () => {
        handleUploadFileClick(".svg", setSelectedFlowSheet, setFormData, null, "uploadFlowSheetName")
    }

    const clearFlowSheetFile = () => {
        setSelectedFlowSheet(null)
        handleChange("uploadFlowSheetName", "")
    }

    return (
        <Grid>
            <Dialog open={isOpen} size='small' dimmer style={{ maxWidth: "512px" }}>
                <DialogHeader username=''>{labels.header}</DialogHeader>
                <DialogDescription>{labels.description}</DialogDescription>
                {isError && <p style={{ color: "red" }}>{isError}</p>}
                <DialogContent>{fieldConfig.map(renderField)}</DialogContent>

                <DialogActions>
                    <Button
                        onClick={handleSubmit}
                        primary
                        centered
                        size='medium'
                        children={labels.confirm}
                        loading={customerLoading}
                        disabled={customerLoading}
                        flexible
                    />
                    <Button onClick={() => onClose(false)} centered size='medium' children={labels.cancel} flexible />
                </DialogActions>
            </Dialog>
        </Grid>
    )
})

export default SiteModal
